import { useState } from "react";
import { Download } from "lucide-react";

import { MonthRangePicker, type MonthRange } from "@/components/cinch/DatePicker";
import { EmptyState } from "@/components/cinch/EmptyState";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  accountingCsvUrl,
  currentMonth,
  fmtMoney,
  fmtMoney0,
  monthLabel,
  monthsAgo,
  useAccounting,
  type AccountingMonth,
} from "../api";

/**
 * Accounting tab — Cody's monthly hand-off.
 *
 * A month is grouped on the *use date*: buying 20 pickleball sets in August is
 * inventory, giving one away in October is October's expense. Shipping follows
 * the gift it belongs to (not the ship date), so one giveaway lands in exactly
 * one month — which is what a journal entry needs.
 */
export function AccountingView() {
  const [months, setMonths] = useState<MonthRange>({
    start: monthsAgo(5),
    end: currentMonth(),
  });
  // The roll-up is always over a bounded span — an unbounded one would make
  // the totals meaningless — so a cleared end falls back to this month.
  const range = {
    from_month: months.start ?? monthsAgo(5),
    to_month: months.end ?? currentMonth(),
  };
  const { data, isLoading, error } = useAccounting(range);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <MonthRangePicker value={months} onChange={setMonths} />
          <span className="text-xs text-muted-foreground">
            Grouped on the date the swag was given away
          </span>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href={accountingCsvUrl(range)}>
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </a>
        </Button>
      </div>

      {isLoading && <Skeleton className="h-64" />}
      {error && <Alert variant="danger">Could not load the roll-up: {String(error)}</Alert>}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <KpiTile
              label="Swag cost"
              value={fmtMoney0(data.swag_cost)}
              sub={`${monthLabel(range.from_month)} – ${monthLabel(range.to_month)}`}
              info="Units given away × the price we paid per unit on the order they came from. Cost is snapshotted when the swag goes out, so re-pricing a later order never restates a month you've already posted."
              infoTitle="How swag cost is calculated"
            />
            <KpiTile
              label="Shipping cost"
              value={fmtMoney0(data.shipping_cost)}
              sub="Split pro-rata across each box's contents"
              info="One box carrying four blankets to one address splits its freight four ways, so each recipient's row carries its true share. The spreadsheet put the whole cost on the first row and left the other three blank."
              infoTitle="How shipping is allocated"
            />
            <KpiTile
              label="Total to expense"
              value={fmtMoney0(data.total_cost)}
              sub="Swag + shipping"
            />
          </div>

          {data.months.length === 0 ? (
            <EmptyState title="Nothing given away in this range">
              Months appear here once swag goes out in them. Widen the range, or
              check the Detail tab for what has been recorded.
            </EmptyState>
          ) : (
            data.months.map((month) => <MonthCard key={month.month} month={month} />)
          )}
        </>
      )}
    </div>
  );
}

function MonthCard({ month }: { month: AccountingMonth }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle className="text-base">{monthLabel(month.month)}</CardTitle>
          <span className="text-sm text-muted-foreground">
            {month.units} unit{month.units === 1 ? "" : "s"} ·{" "}
            {fmtMoney(month.total_cost)}
          </span>
        </div>
        {month.unassigned_units > 0 && (
          <p className="text-xs text-warning">
            {month.unassigned_units} unit(s) worth {fmtMoney(month.unassigned_cost)}{" "}
            have no department — assign them on the Detail tab before posting.
          </p>
        )}
      </CardHeader>
      <CardContent className="pb-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead>QuickBooks class</TableHead>
                <TableHead className="text-right">Units</TableHead>
                <TableHead className="text-right">Swag $</TableHead>
                <TableHead className="text-right">Shipping $</TableHead>
                <TableHead className="text-right">Total $</TableHead>
                <TableHead>Items</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {month.rows.map((row) => (
                <TableRow key={row.department_id ?? "unassigned"}>
                  <TableCell className="font-medium">
                    {row.department_id ? (
                      row.department_name
                    ) : (
                      <StatusBadge value="no_department" />
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.qb_class_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">{row.units}</TableCell>
                  <TableCell className="text-right">{fmtMoney(row.swag_cost)}</TableCell>
                  <TableCell className="text-right">
                    {fmtMoney(row.shipping_cost)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {fmtMoney(row.total_cost)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.items.join(", ")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2}>Month total</TableCell>
                <TableCell className="text-right">{month.units}</TableCell>
                <TableCell className="text-right">{fmtMoney(month.swag_cost)}</TableCell>
                <TableCell className="text-right">
                  {fmtMoney(month.shipping_cost)}
                </TableCell>
                <TableCell className="text-right">{fmtMoney(month.total_cost)}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
